import { useMutation, useQuery } from 'react-query';

import { useHandleErrors, useInfiniteQuery } from 'coreOld/hooks';
import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';
import { importsService } from 'partsOld/services';
import { IPreviewPart } from 'partsOld/types';
import { mapUnknownManufacturers } from 'partsOld/mappers';

export const useGetImportPartsList = (filters?, enabled?) =>
  useInfiniteQuery<any>(importsService.importPartsList, 'importPartsList', filters, enabled);

export const useGetImportPreviewColumns = (filters?, enabled?, onSuccess?) =>
  useInfiniteQuery<string>(
    importsService.importPreviewColumns,
    'importPreviewColumns',
    filters,
    enabled,
    onSuccess
  );

export const useGetImportRows = (filters?, enabled?) =>
  useInfiniteQuery<IPreviewPart>(importsService.importRows, 'importRows', filters, enabled);

export const useGetImportPreviewRows = (filters?, enabled?, onSuccess?) =>
  useInfiniteQuery<any>(
    importsService.importPreview,
    'importPreviewRows',
    filters,
    enabled,
    onSuccess
  );

export const useGetImportValidateManufacturers = (queryParams, onSuccess?) => {
  const { showNotification, clearNotification } = useNotification();

  const { data, refetch, isLoading } = useQuery(
    ['importValidateManufacturers'],
    () => importsService.importValidateManufacturers(queryParams),
    {
      keepPreviousData: true,
      enabled: false,
      onSuccess: (data) => {
        const unknownManufacturers = data.result.unknown;

        clearNotification();

        onSuccess(Object.keys(unknownManufacturers).length);
      },
    }
  );

  const refetchData = () => {
    showNotification({
      message: 'Validating Manufacturers...',
      type: ApiStatuses.LOADING,
      position: NotificationPosition.TOP,
    });

    refetch();
  };

  return {
    result: data && mapUnknownManufacturers(Object.keys(data?.result?.unknown)),
    refetchValidateManufacturers: refetchData,
    isValidateManufacturerLoading: isLoading,
  };
};

export const useGetImportValidateParts = (queryParams, enabled?, onSuccess?) => {
  const { showNotification, clearNotification } = useNotification();

  const { data, refetch, isLoading } = useQuery(
    ['importValidateParts'],
    () => importsService.importValidateParts(queryParams),
    {
      keepPreviousData: true,
      enabled,
      onSuccess: (data) => {
        onSuccess(data.data.unknown_manufacturer);
        clearNotification();
      },
    }
  );

  const refetchData = () => {
    showNotification({
      message: 'Validating Parts...',
      type: ApiStatuses.LOADING,
      position: NotificationPosition.TOP,
    });

    refetch();
  };

  return {
    result: data?.data?.unknown_manufacturer,
    refetchValidateParts: refetchData,
    isValidatePartsLoading: isLoading,
  };
};

export const useImportPartsActions = (onSuccess?) => ({
  ...useImportCreate(onSuccess),
  ...useColumnNameAliases(),
  ...usePreviewSaveParts(),
  ...useImportSaveManufacturers(onSuccess),
  ...useImportParts(),
  ...useImportPartsSaveBulk(),
  ...useImportUomConversions(),
});

const useImportPartsSaveBulk = () => {
  const { errors } = useHandleErrors(undefined, undefined, NotificationPosition.TOP);

  const { mutateAsync } = useMutation(importsService.importPartsSaveBulk, {
    ...errors,
  });

  const partsSaveBulk = (data) => mutateAsync(data);

  return { partsSaveBulk };
};

const useImportCreate = (onSuccess?) => {
  const { errors } = useHandleErrors(undefined, undefined, NotificationPosition.TOP);

  const { mutateAsync, isLoading } = useMutation(importsService.importCreate, {
    ...errors,
    onSuccess,
  });

  const importCreate = (data) => mutateAsync(data);

  return { importCreate, isImportCreateLoading: isLoading };
};

const useColumnNameAliases = () => {
  const { errors } = useHandleErrors(undefined, 'Starting preview...', NotificationPosition.TOP);

  const { mutateAsync, isLoading } = useMutation(importsService.columnNameAliases, {
    ...errors,
  });

  const importColumnNameAliases = (data) => mutateAsync(data);

  return { importColumnNameAliases, isColumnAliasesLoading: isLoading };
};

const usePreviewSaveParts = () => {
  const { errors } = useHandleErrors(undefined, 'Starting preview...', NotificationPosition.TOP);

  const { mutateAsync, isLoading } = useMutation(importsService.previewSaveParts, {
    ...errors,
  });

  const importPreviewSaveParts = (data) => mutateAsync(data);

  return { importPreviewSaveParts, isPreviewSavePartsLoading: isLoading };
};

const useImportSaveManufacturers = (onSuccess?) => {
  const { errors } = useHandleErrors(
    'Success',
    'Saving Manufacturers...',
    NotificationPosition.MODAL,
    ['importValidateParts']
  );

  const { mutateAsync } = useMutation(importsService.importSaveManufacturers, {
    ...errors,
    onSuccess,
  });

  const importSaveManufacturers = (data) => mutateAsync(data);

  return { importSaveManufacturers };
};

const useImportParts = () => {
  const { errors } = useHandleErrors(undefined, 'Starting import...', NotificationPosition.TOP);

  const { mutateAsync } = useMutation(importsService.importParts, {
    ...errors,
  });

  const importParts = (data) => mutateAsync(data);

  return { importParts };
};

const useImportUomConversions = () => {
  const { errors } = useHandleErrors('', 'Reading File...', NotificationPosition.TOP);

  const { mutateAsync } = useMutation(importsService.importUomConversions, {
    ...errors,
  });

  const importUomConversions = (data) => mutateAsync(data);

  return { importUomConversions };
};
